import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, forkJoin, of } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';

import { PageBreadcrumbComponent } from '../../../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ConfirmDialogService } from '../../../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../../../shared/services/toast.service';
import { AuthService } from '../../../../auth-pages/services/auth.service';
import { UserApiService } from '../../../../users/services/user-api.service';
import { CrmUserResponse } from '../../../../users/models/user.model';
import { TicketsConfigApiService } from '../../../services/tickets-config-api.service';
import {
  TicketsTeamDrawerService,
  TicketsTeamMemberDrawerService
} from '../../../services/tickets-config-drawer.services';
import { TicketTeam, TicketTeamMember } from '../../../models/tickets-config.model';

@Component({
  selector: 'app-tickets-teams-detail',
  standalone: true,
  imports: [CommonModule, PageBreadcrumbComponent],
  templateUrl: './teams-detail.component.html'
})
export class TicketsTeamsDetailComponent implements OnInit, OnDestroy {
  team: TicketTeam | null = null;
  members: TicketTeamMember[] = [];
  loading = false;
  error = '';

  /** Mapa intIdUser → nombre legible (INC 11). */
  private usersById = new Map<number, CrmUserResponse>();

  private readonly destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    protected router: Router,
    private configApi: TicketsConfigApiService,
    private teamDrawer: TicketsTeamDrawerService,
    private memberDrawer: TicketsTeamMemberDrawerService,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService,
    private authService: AuthService,
    private userApi: UserApiService
  ) {}

  ngOnInit(): void {
    this.cargarUsuarios();
    const id = Number(this.route.snapshot.paramMap.get('id'));
    if (!id) return;
    this.cargar(id);

    this.teamDrawer.entitySaved$.pipe(takeUntil(this.destroy$)).subscribe((t) => {
      if (t.id === this.team?.id) this.team = t;
    });
    this.memberDrawer.entitySaved$.pipe(takeUntil(this.destroy$)).subscribe(() => this.cargarMembers());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private cargarUsuarios(): void {
    const businessId = this.authService.getCurrentUser()?.businessId || 1;
    this.userApi
      .listActiveByBusiness(businessId)
      .pipe(catchError(() => of([] as CrmUserResponse[])), takeUntil(this.destroy$))
      .subscribe((users) => {
        this.usersById = new Map(users.map((u) => [u.id, u] as const));
      });
  }

  /**
   * Nombre legible del miembro (INC 11). Si el usuario aún no está en el catálogo,
   * se dice "Usuario sin nombre" — nunca el id interno ni el literal en inglés "user".
   */
  memberName(member: TicketTeamMember): string {
    const u = this.usersById.get(member.intIdUser);
    if (!u) return 'Usuario sin nombre';
    return `${u.fullname}${u.lastname ? ' ' + u.lastname : ''}`.trim() || u.mail || 'Usuario sin nombre';
  }

  private cargar(id: number): void {
    this.loading = true;
    this.error = '';
    forkJoin({
      team: this.configApi.getTeamById(id),
      members: this.configApi.getTeamMembers(id).pipe(catchError(() => of([] as TicketTeamMember[])))
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: ({ team, members }) => {
        this.team = team;
        this.members = members;
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar el equipo';
        this.loading = false;
      }
    });
  }

  private cargarMembers(): void {
    if (!this.team?.id) return;
    this.configApi.getTeamMembers(this.team.id)
      .pipe(catchError(() => of([] as TicketTeamMember[])), takeUntil(this.destroy$))
      .subscribe((m) => { this.members = m; });
  }

  editarTeam(): void {
    if (this.team) this.teamDrawer.open({ id: this.team.id });
  }

  agregarMiembro(): void {
    if (this.team) this.memberDrawer.open({ parentId: this.team.id });
  }

  editarMiembro(member: TicketTeamMember): void {
    if (this.team) this.memberDrawer.open({ id: member.id, parentId: this.team.id });
  }

  async quitarMiembro(member: TicketTeamMember): Promise<void> {
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Quitar miembro',
      message: `¿Quitar a ${this.memberName(member)} del equipo?`,
      confirmText: 'Quitar'
    });
    if (!ok) return;
    this.configApi.removeTeamMember(member.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => { this.toast.deleted('Miembro', `Se eliminó "${this.memberName(member)}" correctamente.`); this.cargarMembers(); },
      error: (err) => this.toast.error({ title: 'Error', message: err.error?.message || 'No se pudo quitar el miembro' })
    });
  }

  volver(): void { this.router.navigate(['/tickets/config/teams']); }
}
